import i18n from '@/utils/i18n';
import ProspectImagePlaceholder from '@assets/icons/prospect-image-placeholder.svg';
import React from 'react';
import { Business } from '@/components/locations/types';
import { Button } from '@/components/button';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import { distanceByCountryCode } from '@/utils/distance';
import { formatPhoneNumber, isNotMaliPhoneNumber } from '@/utils/phone-number';
import {
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

const IMG_HEIGHT = 80;

export interface BusinessInfoCardProps {
  business: Business;
  containerStyle?: StyleProp<ViewStyle>;
  createAccount: (business: Business) => void;
  navigateToProspect: (businessUUID: string, businessName: string) => void;
  newAccountYelpBusinessID: string | null;
  supplierCountryCode?: string;
}

export const BusinessInfoCard: React.FC<BusinessInfoCardProps> = ({
  business,
  createAccount,
  containerStyle,
  navigateToProspect,
  newAccountYelpBusinessID,
  supplierCountryCode,
}) => {
  const [styles] = useStyles();

  const isExistingAccount = business.restaurant_uuid;

  const highlightedDetailsText = [
    ...(isExistingAccount ? [i18n.t('existing_account')] : []),
    ...(business.distance
      ? [distanceByCountryCode(business.distance, supplierCountryCode)]
      : []),
    ...(business.categories?.length ? [business.categories[0]] : []),
  ];

  return (
    <TouchableOpacity
      disabled={!isExistingAccount}
      key={business.yelp_business_id}
      onPress={() => {
        navigateToProspect(business.restaurant_uuid!, business.business_name);
      }}
      style={containerStyle}
    >
      <View style={styles.base}>
        {business.image_url ? (
          <CachedImage
            accessibilityLabel={i18n.t('business_image', {
              businessName: business.business_name,
            })}
            contentFit="cover"
            source={{ uri: business.image_url }}
            style={styles.image}
          />
        ) : (
          <ProspectImagePlaceholder
            accessibilityLabel={i18n.t('business_image_placeholder', {
              businessName: business.business_name,
            })}
            height={IMG_HEIGHT}
            style={styles.image}
            width={IMG_HEIGHT}
          />
        )}
        <View style={styles.flex}>
          <Text numberOfLines={1} style={styles.businessName}>
            {business.business_name}
          </Text>
          <Text numberOfLines={1} style={styles.businessInfo}>
            {business.business_display_address}
          </Text>
          {business.phone_number &&
          isNotMaliPhoneNumber(business.phone_number) ? (
            <Text numberOfLines={1} style={styles.businessInfo}>
              {formatPhoneNumber(business.phone_number)}
            </Text>
          ) : null}
          {highlightedDetailsText.length > 0 ? (
            <Text
              numberOfLines={1}
              style={[styles.businessInfo, styles.highlightedDetails]}
            >
              {highlightedDetailsText.join(' • ')}
            </Text>
          ) : null}
        </View>
      </View>
      {!isExistingAccount ? (
        <Button
          loading={newAccountYelpBusinessID === business.yelp_business_id}
          onPress={() => createAccount(business)}
          style={styles.createAccountButton}
          trackingData={{
            name: 'lead_generator.create_account',
          }}
          type="secondary"
        >
          {i18n.t('create_account')}
        </Button>
      ) : null}
    </TouchableOpacity>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flexDirection: 'row',
    gap: 16,
  },
  businessInfo: {
    color: theme.colors.inactiveText,
    fontFamily: theme.fonts.primary,
    fontSize: 14,
    lineHeight: 21,
  },
  businessName: {
    color: theme.sk.color.text.primary,
    fontFamily: theme.fonts.primaryBold,
    fontSize: 16,
  },
  createAccountButton: {
    borderRadius: 8,
    marginTop: 16,
  },
  flex: {
    flex: 1,
    justifyContent: 'flex-start',
    paddingRight: 8,
  },
  highlightedDetails: {
    color: theme.colors.darkGreen,
  },
  image: {
    borderColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 8,
    borderWidth: 1,
    height: IMG_HEIGHT,
    width: IMG_HEIGHT,
  },
}));
